import { useEffect, useRef } from 'react';

type TintableCanvasProps = {
  src: string;
  color: string;
  width: number;
  height: number;
};

const TintIcon = ({ src, color, width, height }: TintableCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !src) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let cancelled = false;

    const dpr = window.devicePixelRatio || 1;

    canvas.width = width * dpr;
    canvas.height = height * dpr;

    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;

      ctx.fillStyle = color;
      ctx.fillRect(0, 0, width, height);

      ctx.globalCompositeOperation = 'destination-in';
      // Fit like objectFit: 'contain' - the previous 0,0,width,height draw
      // stretched non-square logos into the square canvas, distorting them.
      const fitScale = Math.min(width / img.width, height / img.height);
      const drawWidth = img.width * fitScale;
      const drawHeight = img.height * fitScale;
      const dx = (width - drawWidth) / 2;
      const dy = (height - drawHeight) / 2;
      ctx.drawImage(img, dx, dy, drawWidth, drawHeight);

      ctx.globalCompositeOperation = 'source-over';
    };
    img.onerror = () => {
      if (cancelled) return;
      ctx.clearRect(0, 0, width, height);
      return null;
    };

    img.src = src;

    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [src, color, width, height]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        display: 'block',
        imageRendering: 'crisp-edges',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
      }}
    />
  );
};

export default TintIcon;
